// ArticleA3.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";

const THEME = {
  fontFamily:
    "'Prompt', system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial, 'Noto Sans Thai', sans-serif",
  pageBg: "#0b1220",
  textOnDark: "rgba(255,255,255,.92)",
  glass: "rgba(255,255,255,.08)",
  glassStroke: "rgba(255,255,255,.18)",
  primary: "#60a5fa",
  primary2: "#22d3ee",
};

const META = {
  id: "a3",
  title: "สัญญากู้ยืมเงิน: เขียนให้ฟ้องได้ ลดความเสี่ยง และเก็บหนี้ง่ายขึ้น",
  category: "สัญญา",
  dateISO: "2025-07-30",
  readMinsFallback: 8,
  hero:
    "https://images.unsplash.com/photo-1542621334-a254cf47733d?q=80&w=2000&auto=format&fit=crop",
  excerpt:
    "โครงสร้างสัญญากู้ยืมที่ดี บทสำคัญที่ห้ามลืม หลักฐานการรับ-ชำระเงิน ดอกเบี้ย/ผิดนัด ตัวอย่างสัญญาก๊อปได้ และไทม์ไลน์บังคับคดี",
};

export default function ArticleA3() {
  const [progress, setProgress] = useState(0);
  const [activeId, setActiveId] = useState("");
  const [readMins, setReadMins] = useState(META.readMinsFallback);
  const contentRef = useRef(null);
  const heroRef = useRef(null);

  // progress + hero parallax
  useEffect(() => {
    const onScroll = () => {
      const el = contentRef.current;
      if (!el) return;
      const total = el.scrollHeight - window.innerHeight * 0.6;
      const scrolled = Math.min(
        Math.max(window.scrollY - (el.offsetTop - window.innerHeight * 0.4), 0),
        total
      );
      setProgress(total > 0 ? (scrolled / total) * 100 : 0);

      const h = heroRef.current;
      if (h) {
        const y = window.scrollY;
        h.style.setProperty("--parallax", Math.min(y * 0.22, 120) + "px");
        h.style.setProperty("--blur", Math.min(y / 70, 8) + "px");
        h.style.setProperty("--fade", Math.min(y / 420, 0.5));
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // compute reading time
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const words = (el.innerText || "").trim().split(/\s+/).length;
    setReadMins(Math.max(4, Math.round(words / 200)));
  }, []);

  // ScrollSpy
  useEffect(() => {
    const heads = document.querySelectorAll("main article h2[id], main article h3[id]");
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActiveId(e.target.id)),
      { rootMargin: "0px 0px -70% 0px", threshold: 0.01 }
    );
    heads.forEach((h) => io.observe(h));
    return () => io.disconnect();
  }, []);

  const url = useMemo(
    () => (typeof window !== "undefined" ? window.location.href : "/articles/a3"),
    []
  );

  const share = (type) => {
    const text = encodeURIComponent(META.title);
    const u = encodeURIComponent(url);
    let shareUrl = "";
    if (type === "fb") shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${u}`;
    if (type === "tw") shareUrl = `https://twitter.com/intent/tweet?url=${u}&text=${text}`;
    if (type === "ln") shareUrl = `https://social-plugins.line.me/lineit/share?url=${u}`;
    window.open(shareUrl, "_blank", "noopener,noreferrer,width=680,height=520");
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      alert("คัดลอกลิงก์แล้ว");
    } catch {
      alert("คัดลอกไม่สำเร็จ");
    }
  };
  const copyTemplate = (id, label) => {
    const t = document.getElementById(id)?.innerText || "";
    navigator.clipboard
      .writeText(t)
      .then(() => alert(`คัดลอก${label}แล้ว`))
      .catch(() => alert("คัดลอกไม่สำเร็จ"));
  };

  const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString("th-TH", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  return (
    <div className="article-page" style={{ fontFamily: THEME.fontFamily }}>
      <Style />
      <ReadingProgress value={progress} />

      {/* SEO JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: META.title,
            datePublished: META.dateISO,
            dateModified: META.dateISO,
            author: { "@type": "Person", name: "ทีมทนายความ Thai Tax & Law" },
            image: META.hero,
            articleSection: META.category,
            description: META.excerpt,
            mainEntityOfPage: url,
          }),
        }}
      />

      {/* HERO */}
      <header className="hero" ref={heroRef}>
        <img className="hero-media" src={META.hero} alt="" />
        <div className="hero-overlay" />
        <div className="hero-content">
          <nav className="breadcrumbs">
            <a href="/">หน้าแรก</a> <span>•</span> <a href="/articles">บทความ</a>{" "}
            <span>•</span> <span aria-current="page">{META.category}</span>
          </nav>
          <h1 className="hero-title">{META.title}</h1>
          <p className="hero-excerpt">{META.excerpt}</p>
          <div className="hero-meta">
            <span>{fmtDate(META.dateISO)}</span>
            <span>•</span>
            <span>{readMins} นาที</span>
            <span className="cat">{META.category}</span>
          </div>
        </div>
        <div className="hero-bottom-fade" />
      </header>

      {/* LAYOUT */}
      <div className="containerr">
        {/* SIDE */}
        <aside className="side tools">
          <div className="tool-card">
            <div className="tool-title">สารบัญ</div>
            <nav className="toc">
              {[
                ["intro", "หลักคิดก่อนปล่อยกู้"],
                ["law", "พื้นฐานกฎหมาย & หลักฐานสำคัญ"],
                ["must", "ข้อกำหนดที่ต้องมีในสัญญา"],
                ["interest", "ดอกเบี้ย/ผิดนัด/เพดาน"],
                ["guar", "ผู้ค้ำ ประกัน หลักประกัน"],
                ["evidence", "ลายมือชื่อ ดิจิทัล & การโอนเงิน"],
                ["templates", "ตัวอย่างสัญญา (ก๊อปได้)"],
                ["timeline", "ไทม์ไลน์บังคับคดี (ย่อ)"],
                ["whenlawyer", "เมื่อไรควรคุยกับทนายความ"],
                ["faq", "FAQ"],
              ].map(([id, label]) => (
                <a
                  key={id}
                  href={`#${id}`}
                  className={activeId === id ? "active" : ""}
                  onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById(id);
                if (el) {
                    const yOffset = -80; // ชดเชย navbar 80px
                    const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
                    window.scrollTo({ top: y, behavior: "smooth" });
                }
                }}

                >
                  {label}
                </a>
              ))}
            </nav>
          </div>

          
        </aside>

        {/* MAIN */}
        <main>
          <article ref={contentRef}>
            <p className="lede">
              เอกสารสำคัญที่สุดของการปล่อยกู้คือ <b>สัญญากู้ยืมเงิน</b> ที่ชัดเจน
              เก็บหลักฐานการรับ–คืนเงินครบ และมีทางเลือกบังคับคดีเมื่อผิดนัด
            </p>

            <Callout type="info">
              เนื้อหานี้เป็นข้อมูลทั่วไป ไม่ใช่คำปรึกษาเฉพาะเคส
              อัตราดอกเบี้ยตามกฎหมาย/เพดานอาจปรับตามกฎระเบียบล่าสุด
              ควรตรวจสอบอีกครั้งก่อนใช้งานจริงหรือ <a href="/contact">ปรึกษาทนายความ</a>
            </Callout>

            <h2 id="intro">หลักคิดก่อนปล่อยกู้</h2>
            <ul>
              <li>รู้จัก “ความเสี่ยงเครดิต” ของผู้กู้: รายรับ/ภาระหนี้/ทรัพย์ที่ยึดได้</li>
              <li>กำหนดวันครบกำหนดชัดเจน + โครงสร้างผ่อนชำระ (ถ้ามี)</li>
              <li>ตั้งช่องทางการชำระ/แจ้งเตือน/ผิดนัดให้เป็นลายลักษณ์อักษร</li>
              <li>หลีกเลี่ยงการจ่าย/รับเงินสด—ใช้โอน/เช็คเพื่อให้มีร่องรอย</li>
            </ul>

            <h2 id="law">พื้นฐานกฎหมาย & หลักฐานสำคัญ</h2>
            <div className="grid-2">
              <ul className="check">
                <li>คู่สัญญา: ระบุชื่อ–เลขบัตร/หนังสือรับรองนิติบุคคล ชัดเจน</li>
                <li>วัตถุประสงค์/จำนวนเงิน/วันที่ให้กู้</li>
                <li>การรับเงิน: สลิปโอน/เช็ค/ใบสำคัญรับเงิน</li>
              </ul>
              <ul className="check">
                <li>วันครบกำหนด/ตารางผ่อน/ดอกเบี้ย</li>
                <li>กรณีผิดนัด: เบี้ยปรับ ดอกเบี้ยผิดนัด วิธีนับ</li>
                <li>ผู้ค้ำ/หลักประกัน และเงื่อนไขเรียกชำระ</li>
              </ul>
            </div>

            <h2 id="must">ข้อกำหนดที่ต้องมีในสัญญา</h2>
            <ol className="steps">
              <li><b>ยอดกู้ & สกุลเงิน</b> — ตัวเลขและตัวอักษร</li>
              <li><b>ดอกเบี้ยปกติ</b> — วิธีคิด (อัตราคงที่/ลดต้นลดดอก)</li>
              <li><b>ดอกเบี้ยผิดนัด</b> — เริ่มนับเมื่อใด คิดจากยอดใด</li>
              <li><b>กำหนดชำระ</b> — วันครบกำหนด/ตารางงวด</li>
              <li><b>สิทธิเร่งรัด</b> — ผิดนัดงวดเดียวถือว่าทั้งจำนวนถึงกำหนด</li>
              <li><b>ค่าใช้จ่ายฟ้องคดี</b> — เป็นภาระของฝ่ายใด</li>
              <li><b>การชำระก่อนกำหนด</b> — คิดดอกถึงวันชำระจริง</li>
              <li><b>เขตอำนาจศาล</b> — ระบุศาลที่สะดวก (ถ้าเหมาะสม)</li>
            </ol>

            <h2 id="interest">ดอกเบี้ย/ผิดนัด/เพดาน</h2>
            <ul>
              <li>ใช้อัตราที่ไม่เกินเพดานตามกฎหมายที่บังคับใช้ในช่วงเวลานั้น</li>
              <li>ระบุชัดว่า “ดอกเบี้ยผิดนัด” คิดจากยอดต้นเงินที่ถึงกำหนดชำระ</li>
              <li>ระบุการคำนวณเป็น “ต่อปี” และวิธีปัดเศษ/ฐานวัน (เช่น 365 วัน)</li>
            </ul>

            <h2 id="guar">ผู้ค้ำ ประกัน หลักประกัน</h2>
            <div className="grid-2">
              <div>
                <h3>ผู้ค้ำประกัน</h3>
                <ul>
                  <li>ระบุขอบเขตความรับผิด (รวมดอกเบี้ย/เบี้ยปรับ/ค่าใช้จ่าย)</li>
                  <li>ให้ผู้ค้ำลงลายมือชื่อต่อหน้า/ยืนยันตัวตน</li>
                </ul>
              </div>
              <div>
                <h3>หลักประกัน</h3>
                <ul>
                  <li>ระบุรายการทรัพย์ เลขทะเบียน/โฉนด/Serial ให้ครบ</li>
                  <li>ทำสัญญาจำนำ/จำนอง/นส.3/สัญญาโอนสิทธิเรียกร้อง (ถ้าจำเป็น)</li>
                </ul>
              </div>
            </div>

            <h2 id="evidence">ลายมือชื่อ ดิจิทัล & การโอนเงิน</h2>
            <ul>
              <li>ลงลายมือชื่อผู้กู้/ผู้ให้กู้/พยาน 1–2 คน ทุกหน้า (หรือ initial)</li>
              <li>เก็บกำกับ: สำเนาบัตร, หนังสือรับรอง, แผนที่ตั้ง (กรณีนิติบุคคล)</li>
              <li>หากใช้ e-Sign ให้แนบ “Certificate/Log” เป็นไฟล์หลักฐาน</li>
              <li>ชำระ/รับเงินผ่านบัญชีที่ระบุในสัญญาเท่านั้นเพื่อให้ trace ได้</li>
            </ul>

            <h2 id="templates">ตัวอย่างสัญญา (ก๊อปได้)</h2>
            <div className="template">
              <pre id="loan-contract-template">{`สัญญากู้ยืมเงิน

ทำที่ ______________________  วันที่ ____/____/____

ผู้ให้กู้   นาย/นาง/นิติบุคคล ___________________________ เลขที่ประจำตัว/เลขผู้เสียภาษี __________ ที่อยู่ ____________________________
ผู้กู้     นาย/นาง/นิติบุคคล ___________________________ เลขที่ประจำตัว/เลขผู้เสียภาษี __________ ที่อยู่ ____________________________

ข้อ 1 จำนวนเงินกู้ ผู้ให้กู้ตกลงให้กู้เป็นเงินทั้งสิ้น __________ บาท (________________) โดยผู้กู้ได้รับเงินครบถ้วนแล้วผ่านการโอนเข้าบัญชีเลขที่ __________ ธนาคาร __________ เมื่อวันที่ ____/____/____

ข้อ 2 อัตราดอกเบี้ย ดอกเบี้ยปกติ ______% ต่อปี คิดจากเงินต้นคงค้าง (ฐาน 365 วัน) ชำระทุกวันที่ ___ ของเดือน

ข้อ 3 กำหนดชำระคืน เงินต้นและดอกเบี้ยครบกำหนดในวันที่ ____/____/____ หรือเป็นงวดตามตารางแนบท้าย

ข้อ 4 ผิดนัดชำระ หากผู้กู้ค้างชำระเกิน ___ วัน ให้นับเป็นการผิดนัด และผู้กู้ยินยอมชำระดอกเบี้ยผิดนัดในอัตรา ______% ต่อปี นับจากยอดเงินต้นที่ถึงกำหนดชำระจนกว่าจะชำระเสร็จ

ข้อ 5 สิทธิเร่งรัด เมื่อผู้กู้ผิดนัด ผู้ให้กู้มีสิทธิเรียกให้ชำระหนี้ทั้งหมดที่คงค้างในคราวเดียว

ข้อ 6 ค่าใช้จ่าย ผู้กู้ยินยอมรับผิดในค่าใช้จ่ายในการติดตามทวงถามและดำเนินคดีตามที่กฎหมายอนุญาต

ข้อ 7 ผู้ค้ำ/หลักประกัน (ถ้ามี) ________________________________________

ข้อ 8 ข้อตกลงอื่น ๆ ________________________________________________

ลงชื่อ _____________________ ผู้ให้กู้          ลงชื่อ _____________________ ผู้กู้
(_____________________)                     (_____________________)

พยาน 1 _____________________               พยาน 2 _____________________`}</pre>
              <div className="template-actions">
                <button onClick={() => copyTemplate("loan-contract-template", "แบบร่างสัญญากู้")}>คัดลอกสัญญากู้</button>
                <a className="ghost" href="/contact">ให้เราปรับเป็นกรณีของคุณ</a>
              </div>
            </div>

            <div className="template" style={{marginTop:12}}>
              <pre id="promissory-template">{`หนังสือสัญญาใช้เงิน (Promissory Note)

ข้าพเจ้า _____________________ เลขที่บัตร/เลขผู้เสียภาษี __________ ที่อยู่ _____________________
ตกลงเป็นลูกหนี้ของ _____________________ เป็นเงินจำนวน __________ บาท (________________)
โดยสัญญาว่าจะชำระคืนพร้อมดอกเบี้ยในอัตรา ______% ต่อปี ภายในวันที่ ____/____/____
หากผิดนัดยินยอมชำระดอกเบี้ยผิดนัดในอัตรา ______% ต่อปี และค่าใช้จ่ายตามกฎหมาย

ลงชื่อ _____________________ ผู้สัญญาใช้เงิน        วันที่ ____/____/____
พยาน _____________________`}</pre>
              <div className="template-actions">
                <button onClick={() => copyTemplate("promissory-template", "สัญญาใช้เงิน")}>คัดลอกสัญญาใช้เงิน</button>
              </div>
            </div>

            <h2 id="timeline">ไทม์ไลน์บังคับคดี (ย่อ)</h2>
            <ol className="steps">
              <li><b>เตือน & หนังสือทวงถาม</b> — แจ้งยอด/เส้นตาย/ดอกเบี้ย</li>
              <li><b>ฟ้องคดี</b> — แนบสัญญา สลิปโอน ตารางคำนวน</li>
              <li><b>พิพากษา</b> — ขอออกหมายบังคับคดี</li>
              <li><b>ยึด/อายัด</b> — เงินเดือน บัญชี รถ บ้าน หุ้น ฯลฯ ตามควร</li>
            </ol>

            <h2 id="whenlawyer">เมื่อไรควรคุยกับทนายความ</h2>
            <ul>
              <li>วงเงินสูง/ต้องการหลักประกัน/มีผู้ค้ำ</li>
              <li>ผู้กู้มีธุรกิจ/นิติบุคคล/ทรัพย์หลายประเภท</li>
              <li>ต้องการให้สัญญารองรับการบังคับคดีเต็มรูปแบบ</li>
            </ul>

            <h2 id="faq">FAQ</h2>
            <details>
              <summary>ทำไมต้องโอนเงินเข้าบัญชี ไม่ให้เงินสด?</summary>
              <p>เพื่อให้เกิดหลักฐานการรับเงินจริง เชื่อมโยงกับสัญญา ลดข้อโต้แย้งในศาล</p>
            </details>
            <details>
              <summary>ผู้ค้ำถอนตัวได้ไหม?</summary>
              <p>ทำได้เมื่อเจ้าหนี้ยินยอมและทำเอกสารแก้ไขสัญญา หากไม่มีการแก้ไข ผู้ค้ำยังคงรับผิดตามเดิม</p>
            </details>
            <details>
              <summary>สัญญาอิเล็กทรอนิกส์ใช้ได้ไหม?</summary>
              <p>ใช้ได้เมื่อสามารถระบุตัวบุคคลและความยินยอมได้ชัด (เช่น e-sign + audit log) และมีหลักฐานการรับ–ชำระเงินประกอบ</p>
            </details>

            <Callout type="success">
              ต้องการร่างสัญญากู้แบบปรับเฉพาะเคส/พร้อมตารางคำนวณดอกเบี้ย?{" "}
              <a href="/contact">นัดคุยฟรี 15 นาที</a> กับทีมทนายความ & CPA
            </Callout>

            <div className="end-actions">
              <a className="btn-primary" href="/contact">
                ขอคำปรึกษา
                <svg viewBox="0 0 24 24" width="18" height="18">
                  <path d="M5 12h12M13 5l7 7-7 7" stroke="currentColor" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
              <a
                className="ghost"
                href="#top"
                onClick={(e) => {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              >
                กลับขึ้นบน
              </a>
            </div>
          </article>

          {/* RELATED */}
          <section className="related">
            <h3>อ่านต่อ</h3>
            <div className="rel-grid">
              <a className="rel-card" href="/articles/a1">
                <img src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=1200&auto=format&fit=crop" alt="" />
                <div className="meta">
                  <span className="cat">ทวงหนี้</span>
                  <div className="title">คู่มือการติดตามทวงหนี้อย่างถูกกฎหมาย</div>
                </div>
              </a>
              <a className="rel-card" href="/articles/a2">
                <img src="https://www.lawdepot.com/images/lawLibrary/Articles/are-divorce-records-public.jpg" alt="" />
                <div className="meta">
                  <span className="cat">ครอบครัว</span>
                  <div className="title">วางแผนทรัพย์สินก่อนการหย่า: ปกป้องสิทธิ</div>
                </div>
              </a>
              <a className="rel-card" href="/articles/a4">
                <img src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?q=80&w=1200&auto=format&fit=crop" alt="" />
                <div className="meta">
                  <span className="cat">ภาษี</span>
                  <div className="title">ภาษีหนี้สูญ: ตัดหนี้ลูกหนี้เบี้ยวให้ถูกต้อง</div>
                </div>
              </a>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}

/* ---------- Components ---------- */
function ReadingProgress({ value }) {
  return (
    <div className="progress-wrap" aria-hidden="true">
      <div className="progress" style={{ width: `${value}%` }} />
    </div>
  );
}
function Callout({ type = "info", children }) {
  return <div className={`callout ${type}`}>{children}</div>;
}

/* ---------- Styles ---------- */
function Style() {
  return (
    <style>{`
:root{
  --bg:${THEME.pageBg};
  --text:${THEME.textOnDark};
  --glass:${THEME.glass};
  --glass-stroke:${THEME.glassStroke};
  --p1:${THEME.primary};
  --p2:${THEME.primary2};
}
*{box-sizing:border-box}
.article-page{
  color:var(--text);
  background:
    radial-gradient(1200px 600px at 10% -10%, rgba(14,165,233,.12), transparent 60%),
    radial-gradient(1200px 600px at 90% 10%, rgba(99,102,241,.12), transparent 60%),
    var(--bg);
  min-height:100vh;
}

/* progress */
.progress-wrap{position:fixed;top:0;left:0;right:0;height:3px;z-index:100}
.progress{height:3px;background:linear-gradient(90deg,var(--p1),var(--p2));box-shadow:0 0 16px rgba(34,211,238,.35)}

/* hero */
.hero{position:relative;height:58vh;overflow:hidden;isolation:isolate;--parallax:0px;--blur:0px;--fade:0}
.hero-media{position:absolute;inset:-10% -10% -10% -10%;width:120%;height:120%;object-fit:cover;transform:translateY(var(--parallax));filter:blur(var(--blur)) brightness(.85) saturate(.95)}
.hero-overlay{position:absolute;inset:0;background:
  radial-gradient(700px 380px at 18% 32%, rgba(0,0,0,.45), rgba(0,0,0,.7)),
  linear-gradient(90deg, rgba(0,0,0,.6), rgba(0,0,0,.25), rgba(0,0,0,.6));opacity:calc(1 - var(--fade)*.35)}
.hero-bottom-fade{position:absolute;left:0;right:0;bottom:-1px;height:120px;background:linear-gradient(to bottom, transparent, var(--bg));z-index:2}
.hero-content{position:relative;z-index:3;height:100%;display:grid;align-content:center;gap:14px;padding: clamp(20px,5vw,64px);max-width:1100px;margin:0 auto}
.breadcrumbs{opacity:.9;font-size:.95rem}
.breadcrumbs a{color:rgba(255,255,255,.9);text-decoration:none}
.breadcrumbs span{opacity:.6;margin:0 6px}
.hero-title{font-size: clamp(2rem, 4.2vw, 3.6rem);line-height:1.1;margin:0;font-weight:800}
.hero-excerpt{max-width:60ch;opacity:.98}
.hero-meta{display:flex;gap:8px;align-items:center;opacity:.9}
.hero-meta .cat{margin-left:8px;padding:6px 10px;border-radius:999px;background:rgba(255,255,255,.12);border:1px solid rgba(255,255,255,.2)}

/* layout */
.containerr{display:grid;grid-template-columns: 300px 1fr;gap:24px;max-width:1200px;margin:0 auto;padding: 0 clamp(16px,4vw,24px)}
.side{padding-top:28px}
main{padding-top:28px}

/* tools */
.tool-card{position:sticky;top:84px;background:var(--glass);border:1px solid var(--glass-stroke);backdrop-filter: blur(8px);border-radius:16px;padding:14px 14px 12px;margin-bottom:14px}
.tool-title{font-weight:800;margin-bottom:8px}
.toc{display:grid;gap:8px}
.toc a{padding:8px 10px;border-radius:12px;text-decoration:none;color:var(--text);border:1px solid transparent}
.toc a:hover{background:rgba(255,255,255,.08)}
.toc a.active{border-color:rgba(255,255,255,.25);background:linear-gradient(90deg, rgba(96,165,250,.28), rgba(34,211,238,.28))}
.share .share-row{display:grid;gap:8px}
.share button{display:flex;align-items:center;gap:8px;padding:8px 10px;border-radius:12px;border:1px solid var(--glass-stroke);background:rgba(255,255,255,.06);color:var(--text);cursor:pointer}
.share button:hover{background:rgba(255,255,255,.12)}
.author .author-row{display:flex;gap:8px;align-items:center}
.author img{width:40px;height:40px;border-radius:50%;object-fit:cover}
.author .name{font-weight:700}
.author-meta{opacity:.85;margin-top:6px}

/* article */
article{max-width:760px;margin:0 auto 36px}
.lede{font-size:1.1rem;opacity:.98}
h2{font-size:1.6rem;margin:28px 0 12px;font-weight:900}
h3{font-size:1.15rem;margin:14px 0 6px;font-weight:800}
p{line-height:1.8}
ul{padding-left:18px}
ol{padding-left:18px}
.grid-2{display:grid;grid-template-columns:1fr 1fr;gap:14px}
.check li::marker{content:"✔  "}
.no li::marker{content:"✖  "}
.steps>li{margin-bottom:6px}

/* callout */
.callout{border-radius:16px;padding:14px;border:1px solid var(--glass-stroke);background:rgba(255,255,255,.06);margin:14px 0}
.callout.info{background:linear-gradient(180deg, rgba(96,165,250,.15), rgba(255,255,255,.06))}
.callout.tip{background:linear-gradient(180deg, rgba(34,211,238,.15), rgba(255,255,255,.06))}
.callout.success{background:linear-gradient(180deg, rgba(52,211,153,.18), rgba(255,255,255,.06))}

/* template */
.template{border:1px solid var(--glass-stroke);border-radius:16px;overflow:hidden;background:rgba(255,255,255,.04);margin-top:8px}
.template pre{margin:0;padding:14px;line-height:1.7;white-space:pre-wrap}
.template-actions{display:flex;gap:10px;align-items:center;padding:10px;border-top:1px solid var(--glass-stroke)}
.template-actions .ghost{color:var(--text);opacity:.9;text-decoration:none}
.template-actions button{padding:8px 10px;border-radius:12px;border:1px solid var(--glass-stroke);background:rgba(255,255,255,.08);color:var(--text);cursor:pointer}
.template-actions button:hover{background:rgba(255,255,255,.14)}

/* end actions */
.end-actions{display:flex;gap:10px;align-items:center;margin:18px 0 6px}
.btn-primary{display:inline-flex;align-items:center;gap:10px;padding:12px 16px;border-radius:999px;color:#fff;text-decoration:none;background:linear-gradient(90deg,var(--p1),var(--p2));border:1px solid rgba(255,255,255,.25)}
.ghost{padding:10px 12px;border-radius:12px;border:1px solid var(--glass-stroke);text-decoration:none;color:var(--text);background:rgba(255,255,255,.06)}

/* related */
.related{border-top:1px solid rgba(255,255,255,.08);padding-top:20px}
.related h3{font-size:1.2rem;margin:0 0 12px}
.rel-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:14px}
.rel-card{position:relative;overflow:hidden;border-radius:16px;border:1px solid rgba(255,255,255,.12);text-decoration:none;color:#fff;background:#0f1629}
.rel-card img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;filter:brightness(.9);transform:scale(1.02);transition:transform .5s}
.rel-card:hover img{transform:scale(1.06)}
.rel-card .meta{position:relative;padding:12px;background:linear-gradient(180deg, rgba(0,0,0,.0), rgba(0,0,0,.75));margin-top:110px}
.rel-card .cat{display:inline-block;padding:6px 10px;border-radius:999px;background:rgba(255,255,255,.16);border:1px solid rgba(255,255,255,.28);margin-bottom:6px}
.rel-card .title{font-weight:800}

/* responsive */
@media (max-width: 1024px){
  .containerr{grid-template-columns:1fr}
  .side{order:2}
  article{max-width:680px}
}
@media (max-width:640px){
  .hero{height:54vh}
  .grid-2{grid-template-columns:1fr}
  .rel-grid{grid-template-columns:1fr}
}
    `}</style>
  );
}
